// src/pages/admin/AdminListingDetail.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Ruler,
  Calendar,
  IndianRupee,
  Image as ImageIcon,
  AlertCircle,
  RefreshCw,
  Tag,
  FileText,
  Building2,
  CheckCircle2,
  XCircle,
  Clock3,
  Navigation,
  StickyNote,
  Layers,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { getAdminListingById } from '../../services/Adminlistings.api';
import { AdminListingDetail as AdminListingDetailModel } from '../../types/adminlistingsmodel';
type ListingStatusMeta = { label: string; classes: string; icon: React.ElementType };
interface AdminListingDetailProps {
  listingId: string | null;
  open: boolean;
  onClose: () => void;
}

/* ---------- helpers ---------- */

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return `₹${value.toLocaleString('en-IN')}`;
};

const statusConfig: Record<string, ListingStatusMeta> = {
  approved: { label: 'Approved', classes: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: CheckCircle2 },
  pending: { label: 'Pending review', classes: 'bg-amber-50 text-amber-700 ring-amber-600/20', icon: Clock3 },
  rejected: { label: 'Rejected', classes: 'bg-rose-50 text-rose-700 ring-rose-600/20', icon: XCircle },
};

/* ---------- small building blocks ---------- */

const SectionHeading: React.FC<{ icon: React.ElementType; title: string }> = ({
  icon: Icon,
  title,
}) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-50">
      <Icon className="w-3.5 h-3.5 text-indigo-600" />
    </div>
    <h3 className="text-sm font-semibold text-slate-800 tracking-wide">{title}</h3>
  </div>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 last:border-b-0">
    <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
      {label}
    </span>
    <span className="text-sm text-slate-700 font-medium break-words">{value ?? '—'}</span>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-sm ${className}`}
  >
    {children}
  </div>
);

/* ---------- skeleton ---------- */

const DetailSkeleton: React.FC = () => (
  <div className="p-5 space-y-5 animate-pulse">
    <div className="h-40 bg-slate-100 rounded-2xl" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="h-32 bg-slate-100 rounded-2xl" />
      <div className="h-32 bg-slate-100 rounded-2xl" />
    </div>
    <div className="h-24 bg-slate-100 rounded-2xl" />
    <div className="h-24 bg-slate-100 rounded-2xl" />
  </div>
);

/* ---------- error state ---------- */

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-rose-50 mb-4">
      <AlertCircle className="w-6 h-6 text-rose-500" />
    </div>
    <p className="text-sm font-semibold text-slate-800 mb-1">Couldn't load this listing</p>
    <p className="text-sm text-slate-500 mb-5 max-w-xs">{message}</p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
    >
      <RefreshCw className="w-4 h-4" />
      Try again
    </button>
  </div>
);

/* ---------- photo lightbox ---------- */

const PhotoLightbox: React.FC<{
  photos: string[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}> = ({ photos, index, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((index + 1) % photos.length);
      if (e.key === 'ArrowLeft') onNavigate((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [index, photos.length, onClose, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {photos.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index - 1 + photos.length) % photos.length);
          }}
          className="absolute left-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      <img
        src={photos[index]}
        alt={`Listing photo ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />

      {photos.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index + 1) % photos.length);
          }}
          className="absolute right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-xs font-medium">
        {index + 1} / {photos.length}
      </div>
    </div>
  );
};

/* ---------- main component ---------- */

const AdminListingDetail: React.FC<AdminListingDetailProps> = ({
  listingId,
  open,
  onClose,
}) => {
  const [data, setData] = useState<AdminListingDetailModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminListingById(listingId);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch listing details.');
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (open && listingId) {
      fetchDetail();
    }
    if (!open) {
      // reset when closed so next open starts fresh
      setData(null);
      setError(null);
      setLightboxIndex(null);
    }
  }, [open, listingId, fetchDetail]);

  // lock background scroll while the modal is open
  useEffect(() => {
    if (open) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxIndex === null) onClose();
    };
    if (open) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose, lightboxIndex]);

  if (!open) return null;

  const listing = data?.listing;
  const status = listing ? statusConfig[listing.approvalStatus] ?? statusConfig.pending : null;
  const StatusIcon = status?.icon ?? Clock3;

  const charges = listing?.additionalCharges;
  const hasExtraCharges =
    charges &&
    (charges.wallPaintingCharge > 0 ||
      charges.installationCharge > 0 ||
      charges.removalCharge > 0 ||
      charges.otherCharge > 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* modal */}
      <div className="relative z-[101] w-full max-w-4xl max-h-[92vh] bg-slate-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-[fadeIn_0.15s_ease-out]">
        {/* header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 py-4 border-b border-slate-200 bg-white shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900 truncate max-w-[280px] sm:max-w-md">
                {listing?.title || (loading ? 'Loading listing…' : 'Listing details')}
              </h2>
              {status && (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${status.classes}`}
                >
                  <StatusIcon className="w-3 h-3" />
                  {status.label}
                </span>
              )}
            </div>
            {listing && (
              <p className="text-xs text-slate-400 mt-1 font-mono">
                ID: {listing.id} &nbsp;•&nbsp; S.No {listing.sno}
              </p>
            )}
          </div>

          <button
            onClick={onClose}
            className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* scrollable body */}
        <div
          className="overflow-y-auto grow px-5 sm:px-6 py-5
          [&::-webkit-scrollbar]:w-2
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-slate-300
          [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:bg-slate-400"
        >
          {loading && <DetailSkeleton />}

          {!loading && error && <ErrorState message={error} onRetry={fetchDetail} />}

          {!loading && !error && data && listing && (
            <div className="space-y-5">
              {/* photos */}
              {listing.photos && listing.photos.length > 0 ? (
                <Card className="p-2 sm:p-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {listing.photos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setLightboxIndex(idx)}
                        className="relative aspect-square rounded-xl overflow-hidden group ring-1 ring-slate-200"
                      >
                        <img
                          src={photo}
                          alt={`Listing ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </button>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="flex items-center gap-3 text-slate-400">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-sm">No photos uploaded for this listing</span>
                </Card>
              )}

              {/* overview + location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <SectionHeading icon={Layers} title="Overview" />
                  <InfoRow label="Media type" value={listing.mediaType} />
                  <InfoRow
                    label="Dimensions"
                    value={
                      <span className="inline-flex items-center gap-1">
                        <Ruler className="w-3.5 h-3.5 text-slate-400" />
                        {listing.width} × {listing.height} ft
                      </span>
                    }
                  />
                  <InfoRow label="Location type" value={data.locationTypeName} />
                  <InfoRow label="Traffic level" value={data.locationTypetrafficLevel} />
                </Card>

                <Card>
                  <SectionHeading icon={MapPin} title="Location" />
                  <InfoRow
                    label="Address"
                    value={`${listing.area}, ${listing.landmark ? listing.landmark + ', ' : ''}${listing.city}`}
                  />
                  <InfoRow label="State / Country" value={`${listing.state}, ${listing.country}`} />
                  <InfoRow label="Pin code" value={listing.pinCode} />
                  <InfoRow
                    label="Coordinates"
                    value={
                      <span className="inline-flex items-center gap-1 font-mono text-xs">
                        <Navigation className="w-3.5 h-3.5 text-slate-400" />
                        {listing.latitude}, {listing.longitude}
                      </span>
                    }
                  />
                </Card>
              </div>

              {/* availability + pricing */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <SectionHeading icon={Calendar} title="Availability" />
                  <InfoRow label="Available from" value={formatDate(listing.availableFrom)} />
                  <InfoRow label="Available to" value={formatDate(listing.availableTo)} />
                </Card>

                <Card>
                  <SectionHeading icon={IndianRupee} title="Pricing" />
                  <InfoRow
                    label="Expected rent"
                    value={
                      <span className="text-emerald-700 font-bold">
                        {formatCurrency(listing.expectedRent)}
                      </span>
                    }
                  />
                  <InfoRow label="Commission %" value={`${data.locationTypeCommissionPercentage}%`} />
                </Card>
              </div>

              {/* additional charges */}
              {hasExtraCharges && charges && (
                <Card>
                  <SectionHeading icon={Tag} title="Additional charges" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {charges.wallPaintingCharge > 0 && (
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-[11px] text-slate-400 mb-1">Wall painting</p>
                        <p className="text-sm font-bold text-slate-800">
                          {formatCurrency(charges.wallPaintingCharge)}
                        </p>
                      </div>
                    )}
                    {charges.installationCharge > 0 && (
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-[11px] text-slate-400 mb-1">Installation</p>
                        <p className="text-sm font-bold text-slate-800">
                          {formatCurrency(charges.installationCharge)}
                        </p>
                      </div>
                    )}
                    {charges.removalCharge > 0 && (
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-[11px] text-slate-400 mb-1">Removal</p>
                        <p className="text-sm font-bold text-slate-800">
                          {formatCurrency(charges.removalCharge)}
                        </p>
                      </div>
                    )}
                    {charges.otherCharge > 0 && (
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-[11px] text-slate-400 mb-1">
                          {charges.otherChargeDescription || 'Other'}
                        </p>
                        <p className="text-sm font-bold text-slate-800">
                          {formatCurrency(charges.otherCharge)}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* description */}
              {listing.description && (
                <Card>
                  <SectionHeading icon={FileText} title="Description" />
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {listing.description}
                  </p>
                </Card>
              )}

              {/* provider */}
              <Card>
                <SectionHeading icon={Building2} title="Provider" />
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <InfoRow
                    label="Name"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {data.providerName}
                      </span>
                    }
                  />
                  <InfoRow
                    label="Mobile"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        {data.providerMobile}
                      </span>
                    }
                  />
                </div>
              </Card>

              {/* location type description */}
              {data.locationTypeDescription && (
                <Card>
                  <SectionHeading icon={Layers} title="Location type notes" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {data.locationTypeDescription}
                  </p>
                </Card>
              )}

              {/* admin note */}
              {listing.adminNote && (
                <Card className="bg-amber-50/60 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <StickyNote className="w-4 h-4 text-amber-600" />
                    <h3 className="text-sm font-semibold text-amber-800">Admin note</h3>
                  </div>
                  <p className="text-sm text-amber-800/90 leading-relaxed">
                    {listing.adminNote}
                  </p>
                </Card>
              )}

              {/* metadata */}
              <Card className="bg-slate-50/60">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-slate-400">
                  <div>
                    <span className="block uppercase tracking-wide mb-0.5">Created</span>
                    <span className="text-slate-600 font-medium">
                      {formatDate(listing.cdate)} {listing.ctime}
                    </span>
                  </div>
                  <div>
                    <span className="block uppercase tracking-wide mb-0.5">Updated</span>
                    <span className="text-slate-600 font-medium">{formatDate(listing.edate)}</span>
                  </div>
                  <div>
                    <span className="block uppercase tracking-wide mb-0.5">Flag</span>
                    <span className="text-slate-600 font-medium">{listing.dflag}</span>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {lightboxIndex !== null && listing?.photos && (
        <PhotoLightbox
          photos={listing.photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
};

export default AdminListingDetail;